<template>
  <v-container fluid>
    <v-alert type="info" outlined dense class="mt-2">
      A machine token is what one county workstation uses to upload files with the
      County Toolkit's Push to Cloud. It is not a user account: it has no password,
      and it can only do what its <strong>scopes</strong> allow. One token per
      workstation &mdash; pausing or revoking one stops that desk and no other.
      <div class="mt-2">
        To set up a new desk, use <strong>Set Up a Workstation</strong> below: it gives you an
        installer that collects its own token, so nobody has to copy one by hand.
      </div>
    </v-alert>

    <MachineEnrollmentsList />

    <v-data-table
      :headers="headers"
      :items="machineTokens"
      :items-per-page="10"
      class="elevation-1 mt-2"
      dense
      disable-pagination
      hide-default-footer
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>Machine Tokens</v-toolbar-title>
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-checkbox
            id="machineTokenShowRevoked"
            v-model="showRevoked"
            label="Show revoked"
            dense
            hide-details
            class="mt-4"
            @change="reload"
          ></v-checkbox>
          <v-spacer></v-spacer>
          <v-btn
            id="machineTokenNewBtn"
            small
            color="primary"
            @click="mintDialog = true"
          >
            New Machine Token
          </v-btn>
        </v-toolbar>
      </template>

      <template v-slot:[`item.scopes`]="{ item }">
        <span>{{ (item.scopes || []).join(', ') || '&mdash;' }}</span>
      </template>
      <template v-slot:[`item.wkst`]="{ item }">
        <span>{{ item.wkst || '—' }}</span>
      </template>
      <template v-slot:[`item.createdAt`]="{ item }">
        <span>{{ shortDate(item.createdAt) }}</span>
      </template>
      <template v-slot:[`item.lastUsedAt`]="{ item }">
        <span>{{ shortDate(item.lastUsedAt) || 'never used' }}</span>
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <span :class="statusClass(item)">{{ statusLabel(item) }}</span>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-icon
          v-if="item.status === 'active'"
          small
          class="mr-2 machine-token-pause"
          title="Pause this token; it can be resumed later"
          @click="lifecycle('pause', item)"
        >
          {{ icons.mdiPause }}
        </v-icon>
        <v-icon
          v-if="item.status === 'paused'"
          small
          class="mr-2 machine-token-resume"
          title="Return this token to service"
          @click="lifecycle('resume', item)"
        >
          {{ icons.mdiPlay }}
        </v-icon>
        <v-icon
          v-if="item.status !== 'revoked'"
          small
          class="mr-2 machine-token-revoke"
          title="Revoke this token for good"
          @click="lifecycle('revoke', item)"
        >
          {{ icons.mdiCancel }}
        </v-icon>
        <v-icon
          v-if="item.status === 'revoked'"
          small
          class="mr-2 machine-token-delete"
          title="Remove this revoked token from the list"
          @click="remove(item)"
        >
          {{ icons.mdiDelete }}
        </v-icon>
      </template>
    </v-data-table>

    <MachineTokenMintDialog v-model="mintDialog" />
  </v-container>
</template>

<script>
import {
  mdiCancel, mdiDelete, mdiPause, mdiPlay,
} from '@mdi/js';
import MachineEnrollmentsList from './MachineEnrollmentsList.vue';
import MachineTokenMintDialog from './MachineTokenMintDialog.vue';

/**
 * The Machine Tokens tab.
 *
 * Tokens are listed directly rather than hanging off an account: a machine token references no
 * Auth document (see docs/plans/MACHINE-TOKENS-STANDALONE-PLAN.md in regov-county-toolkit).
 *
 * Two rules the server enforces and this screen respects:
 *  - **Revoked rows stay listed** (behind "Show revoked"): the row is the record of which desk was
 *    cut off and when, which is what support needs after the fact.
 *  - **Delete is offered only on a revoked token.** Deleting an active one looks identical from the
 *    machine's point of view but destroys that record, so the server refuses it and this only shows
 *    the action where it can succeed.
 *
 * Minting a token here is now the *manual* path, for a desk that already has the toolkit installed
 * or for somebody who wants the credential itself. The guided path is MachineEnrollmentsList above,
 * which issues a one-time code and never shows a token to anybody.
 */
export default {
  name: 'MachineTokensList',
  components: {
    MachineEnrollmentsList,
    MachineTokenMintDialog,
  },
  data: () => ({
    icons: {
      mdiCancel, mdiDelete, mdiPause, mdiPlay,
    },
    mintDialog: false,
    showRevoked: true,
    headers: [
      { text: 'Label', value: 'label' },
      { text: 'Token id', value: 'tokenId' },
      { text: 'Scopes', value: 'scopes' },
      { text: 'wkst #', value: 'wkst' },
      { text: 'Created', value: 'createdAt' },
      { text: 'Last used', value: 'lastUsedAt' },
      { text: 'Status', value: 'status', sortable: false },
      { text: 'Action', value: 'actions', sortable: false },
    ],
  }),
  computed: {
    machineTokens() {
      return this.$store.state.UserAdministration.machineTokens || [];
    },
  },
  created() {
    this.reload();
    // The picker in the mint dialog is built from the server's list, so an added scope shows up
    // without a frontend release.
    this.$store.dispatch('UserAdministration/loadMachineTokenScopes');
  },
  methods: {
    reload() {
      this.$store.dispatch('UserAdministration/loadMachineTokens', {
        includeRevoked: this.showRevoked,
      });
    },
    shortDate(value) {
      if (!value) return '';
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return String(value);
      return parsed.toLocaleString();
    },
    statusLabel(item) {
      if (item.status === 'revoked') return 'Revoked';
      if (item.status === 'paused') return 'Paused';
      return 'Active';
    },
    statusClass(item) {
      if (item.status === 'revoked') return 'red--text';
      if (item.status === 'paused') return 'orange--text';
      return 'green--text';
    },
    confirmations(action, item) {
      const name = item.label || item.tokenId;
      return {
        pause: `Pause the machine token "${name}"? That workstation stops uploading when its `
          + 'current sign-in expires. You can resume it later.',
        resume: `Resume the machine token "${name}"? That workstation starts uploading again.`,
        revoke: `Revoke the machine token "${name}"? That workstation stops uploading when its `
          + 'current sign-in expires, and the token cannot be un-revoked.',
      }[action];
    },
    async lifecycle(action, item) {
      // Resume is harmless, but pause and revoke stop a desk uploading, and revoke is one-way.
      if (!window.confirm(this.confirmations(action, item))) return;
      await this.$store.dispatch('UserAdministration/machineTokenLifecycle', {
        action,
        tokenId: item.tokenId,
      });
    },
    async remove(item) {
      const name = item.label || item.tokenId;
      const message = `Delete the revoked machine token "${name}"? This removes the record of `
        + 'when that workstation was cut off.';
      if (!window.confirm(message)) return;
      await this.$store.dispatch('UserAdministration/deleteMachineToken', {
        tokenId: item.tokenId,
      });
    },
  },
};
</script>
